"use strict";
// ── Distance & area ────────────────────────────────────────────────────────────
Object.defineProperty(exports, "__esModule", { value: true });
exports.haversineMetres = haversineMetres;
exports.polygonAreaM2 = polygonAreaM2;
exports.midpoint = midpoint;
exports.fmtDist = fmtDist;
exports.generateCirclePoints = generateCirclePoints;
exports.decodeCoordinates = decodeCoordinates;
exports.encodeCoordinates = encodeCoordinates;
exports.poiCenter = poiCenter;
exports.gameRecordCenter = gameRecordCenter;
exports.extractVerticesFromFeatures = extractVerticesFromFeatures;
exports.hexToRgba = hexToRgba;
/** Haversine distance between two [lng, lat] points, in metres. */
function haversineMetres([lng1, lat1], [lng2, lat2]) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
/**
 * Approximate planar area of a polygon ring (as [lng, lat][] vertices) in m².
 * Uses the shoelace formula scaled by a per-latitude degree-to-metre factor.
 * Sufficient precision for UI display; not suitable for geodetic computation.
 */
function polygonAreaM2(coords) {
    let area = 0;
    const n = coords.length;
    for (let i = 0; i < n; i++) {
        const [x1, y1] = coords[i];
        const [x2, y2] = coords[(i + 1) % n];
        area += (x2 - x1) * (y2 + y1);
    }
    const avgLat = coords.reduce((s, c) => s + c[1], 0) / n;
    const mPerDegLat = 111320;
    const mPerDegLng = 111320 * Math.cos((avgLat * Math.PI) / 180);
    return Math.abs(area / 2) * mPerDegLat * mPerDegLng;
}
// ── Geometry helpers ───────────────────────────────────────────────────────────
/** Midpoint of two coordinate pairs. */
function midpoint([x1, y1], [x2, y2]) {
    return [(x1 + x2) / 2, (y1 + y2) / 2];
}
/** Format a distance in metres as a short German-style string. */
function fmtDist(m) {
    return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;
}
/**
 * Approximate a circle as a closed polygon ring.
 * @param center   [lng, lat] of the centre
 * @param radiusM  radius in metres
 * @param numPoints number of ring vertices (min 6 for a hexagon)
 */
function generateCirclePoints(center, radiusM, numPoints) {
    const R = 6371000;
    const [lng0, lat0] = center;
    const lat0Rad = (lat0 * Math.PI) / 180;
    const lng0Rad = (lng0 * Math.PI) / 180;
    const d = radiusM / R;
    const pts = [];
    for (let i = 0; i < numPoints; i++) {
        const bearing = (2 * Math.PI * i) / numPoints;
        const lat = Math.asin(Math.sin(lat0Rad) * Math.cos(d) +
            Math.cos(lat0Rad) * Math.sin(d) * Math.cos(bearing));
        const lng = lng0Rad + Math.atan2(Math.sin(bearing) * Math.sin(d) * Math.cos(lat0Rad), Math.cos(d) - Math.sin(lat0Rad) * Math.sin(lat));
        pts.push([(lng * 180) / Math.PI, (lat * 180) / Math.PI]);
    }
    return pts;
}
// ── Coordinate encoding ────────────────────────────────────────────────────────
/**
 * Decode the app's "lat lng lat lng …" encoded-content string into
 * Mapbox-compatible [lng, lat][] pairs.
 */
function decodeCoordinates(encoded) {
    const parts = encoded.trim().split(/\s+/).map(Number);
    const coords = [];
    for (let i = 0; i + 1 < parts.length; i += 2) {
        const lat = parts[i];
        const lng = parts[i + 1];
        if (!isNaN(lat) && !isNaN(lng))
            coords.push([lng, lat]);
    }
    return coords;
}
/**
 * Encode [lng, lat][] vertices back to the app's "lat lng lat lng …" format.
 * (Inverse of decodeCoordinates.)
 */
function encodeCoordinates(vertices) {
    return vertices.map(([lng, lat]) => `${lat} ${lng}`).join(' ');
}
/**
 * A representative [lng, lat] for a POI, whatever its geometry.
 *
 * Points carry `locationCoordinates`, but lines and polygons only have
 * `encodedContent` (multi-part polygons separated by ' | '), so they'd otherwise
 * resolve to no location at all. For those we use the mean of the vertices — the
 * same thing turf's `centroid` does. Good enough to hang a marker or a work order on;
 * it isn't the true area centre of mass, and a closed ring's repeated first vertex
 * biases it very slightly.
 */
function poiCenter(poi) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!poi)
        return null;
    const lat = (_b = (_a = poi.locationCoordinates) === null || _a === void 0 ? void 0 : _a.latitude) !== null && _b !== void 0 ? _b : (_c = poi.location) === null || _c === void 0 ? void 0 : _c.latitude;
    const lng = (_e = (_d = poi.locationCoordinates) === null || _d === void 0 ? void 0 : _d.longitude) !== null && _e !== void 0 ? _e : (_f = poi.location) === null || _f === void 0 ? void 0 : _f.longitude;
    if (lat != null && lng != null)
        return [lng, lat];
    const encoded = (_g = poi.encodedContent) !== null && _g !== void 0 ? _g : '';
    if (!encoded)
        return null;
    const coords = encoded
        .split(' | ')
        .flatMap(part => decodeCoordinates(part.trim()));
    if (!coords.length)
        return null;
    const [sumLng, sumLat] = coords.reduce(([accLng, accLat], [lngV, latV]) => [accLng + lngV, accLat + latV], [0, 0]);
    return [sumLng / coords.length, sumLat / coords.length];
}
/** A representative [lng, lat] for a game record (kill site, else plain location). */
function gameRecordCenter(record) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (!record)
        return null;
    const lat = (_e = (_c = (_b = (_a = record.placeOfKilling) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.latitude) !== null && _c !== void 0 ? _c : (_d = record.locationCoordinates) === null || _d === void 0 ? void 0 : _d.latitude) !== null && _e !== void 0 ? _e : (_f = record.location) === null || _f === void 0 ? void 0 : _f.latitude;
    const lng = (_l = (_j = (_h = (_g = record.placeOfKilling) === null || _g === void 0 ? void 0 : _g.location) === null || _h === void 0 ? void 0 : _h.longitude) !== null && _j !== void 0 ? _j : (_k = record.locationCoordinates) === null || _k === void 0 ? void 0 : _k.longitude) !== null && _l !== void 0 ? _l : (_m = record.location) === null || _m === void 0 ? void 0 : _m.longitude;
    return (lat != null && lng != null) ? [lng, lat] : null;
}
// ── GeoJSON vertex extraction ──────────────────────────────────────────────────
/**
 * Flatten all vertices from a GeoJSON feature collection into [lng, lat][] pairs.
 * Supports Point, LineString, and Polygon geometries.
 * Polygon rings: the duplicated closing vertex is omitted.
 */
function extractVerticesFromFeatures(features) {
    var _a;
    const out = [];
    for (const f of features) {
        const g = f.geometry;
        if (!g)
            continue;
        if (g.type === 'Point') {
            const c = g.coordinates;
            if (c.length >= 2)
                out.push([c[0], c[1]]);
        }
        else if (g.type === 'LineString') {
            for (const c of g.coordinates) {
                if (c.length >= 2)
                    out.push([c[0], c[1]]);
            }
        }
        else if (g.type === 'Polygon') {
            const ring = (_a = g.coordinates[0]) !== null && _a !== void 0 ? _a : [];
            for (let i = 0; i < ring.length; i++) {
                // Skip the ring's duplicated closing vertex
                if (i === ring.length - 1 &&
                    ring[i][0] === ring[0][0] &&
                    ring[i][1] === ring[0][1])
                    continue;
                out.push([ring[i][0], ring[i][1]]);
            }
        }
    }
    return out;
}
// ── Colour helpers ─────────────────────────────────────────────────────────────
/**
 * Convert a 6- or 8-digit hex colour string to a CSS rgba() value.
 * Passthrough for any value that doesn't match (returns as-is).
 * Examples: "#FF0000" → "rgba(255,0,0,1)", "#FF000080" → "rgba(255,0,0,0.5)"
 */
function hexToRgba(hex) {
    if (!hex)
        return hex;
    const m8 = hex.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (m8) {
        const r = parseInt(m8[1], 16);
        const g = parseInt(m8[2], 16);
        const b = parseInt(m8[3], 16);
        const a = Math.round((parseInt(m8[4], 16) / 255) * 100) / 100;
        return `rgba(${r},${g},${b},${a})`;
    }
    const m6 = hex.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (m6) {
        return `rgba(${parseInt(m6[1], 16)},${parseInt(m6[2], 16)},${parseInt(m6[3], 16)},1)`;
    }
    return hex;
}
